import { Button } from '@immo/ui-kit';
import { FileIcon, XIcon } from '@/components/layout/icons';

type Translate = (key: string, values?: Record<string, string | number>) => string;

// Human-readable byte size. Base-1000 (MB, not MiB) to match the "25 MB" copy
// users see, with one decimal for KB and up.
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1000;
  let unitIndex = 0;

  while (value >= 1000 && unitIndex < units.length - 1) {
    value = value / 1000;
    unitIndex += 1;
  }

  return `${value.toFixed(1)} ${units[unitIndex]}`;
}

export type ComposerAttachmentsProps = {
  readonly files: ReadonlyArray<File>;
  readonly onChange: (files: ReadonlyArray<File>) => void;
  readonly t: Translate;
  // Bytes already committed as forwarded attachments — folded into the running
  // total so the member sees the true combined size the parent guards on send.
  readonly baselineBytes?: number;
};

// The attachment list only — adding files is triggered from the body toolbar and
// the footer (the composer owns the hidden file input); this renders the chosen
// files with a remove control and the running total.
export function ComposerAttachments({ files, onChange, t, baselineBytes = 0 }: ComposerAttachmentsProps) {
  const totalBytes = baselineBytes + files.reduce((sum, file) => sum + file.size, 0);

  const removeAt = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  if (files.length === 0 && totalBytes === 0) return null;

  return (
    // Grey rule up top sets the attachment tray apart from the body editor above.
    <div className="space-y-2 border-t border-border pt-3">
      {files.length > 0 && (
        // Cap the list height so a long pile of attachments scrolls internally
        // instead of growing unbounded and shoving the From/To/Subject fields
        // off the top of the composer.
        <ul className="max-h-40 space-y-1 overflow-y-auto">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 rounded-md border border-border bg-surface px-2 py-1 text-xs"
            >
              <FileIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-text-primary">{file.name}</span>
              <span className="tabular-nums text-text-tertiary">{formatBytes(file.size)}</span>
              {/* `variant="ghost"` per the slice contract, pinned back from the
                  kit's 36px to 20px: the row is a `py-1 text-xs` chip roughly
                  24px tall, and the token footprint would have grown every
                  attachment row by half its height. `size={null}` — not
                  `size="icon"` plus an `h-5 w-5` override — because until
                  IMM-785 `cn` did not classify `h-control-md` as a height, so a
                  `className` override never beat it (BL-103); `size={null}`
                  stays because it states that intent directly. */}
              <Button
                type="button"
                variant="ghost"
                size={null}
                aria-label={t('attachment-remove')}
                onClick={() => removeAt(index)}
                className="h-5 w-5 shrink-0 text-text-tertiary hover:bg-transparent hover:text-text-primary"
              >
                <XIcon className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {totalBytes > 0 && (
        <div className="flex items-center justify-between text-xs text-text-tertiary">
          <span>{files.length > 0 ? t('attachments-count', { count: files.length }) : null}</span>
          <span>{t('attachments-total', { size: formatBytes(totalBytes) })}</span>
        </div>
      )}
    </div>
  );
}
